// src/pages/LoaderDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from "../api/baseURL";
import LoaderRequestsQueue from '../components/LoaderRequestsQueue';
import { FaTruck, FaTasks } from 'react-icons/fa';

const VEHICLE_TYPE_LABELS = {
  flatbed: 'Plateau',
  enclosed: 'Fermé',
  tow_truck: 'Dépanneuse',
  multi_carrier: 'Porte-voitures',
};

const Detail = ({ label, value, strong }) => (
  <div className="flex items-center justify-between px-6 py-3.5">
    <dt className="text-sm text-gray-500">{label}</dt>
    <dd className={`text-sm ${strong ? 'font-bold text-gray-900' : 'text-gray-800'}`}>
      {value || <span className="text-gray-300">—</span>}
    </dd>
  </div>
);

const LoaderDashboard = () => {
  // A loader operates a single truck, stored on their own LoaderProfile —
  // there is no /loader/vehicles endpoint (the old call here always failed
  // and left the tab permanently empty).
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [view, setView] = useState('jobs'); // 'jobs' or 'fleet'
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API_URL}/loader/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(res.data?.profile || res.data);
        setProfileError('');
      } catch (err) {
        setProfileError("Aucun profil remorqueur trouvé. Complétez votre profil pour recevoir des missions.");
      }
    };

    if (view === 'fleet') {
      fetchProfile();
    }
  }, [token, view]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <header className="mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Loader Dashboard</h1>
          <p className="text-sm text-gray-500">Manage your jobs and fleet</p>
        </div>

        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setView('jobs')}
            className={`px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition ${view === 'jobs' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaTasks /> Jobs Queue
          </button>
          <button
            onClick={() => setView('fleet')}
            className={`px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 transition ${view === 'fleet' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaTruck /> Mon camion
          </button>
        </div>
      </header>

      {view === 'jobs' ? (
        <LoaderRequestsQueue />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden fade-in">
          <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
            <h2 className="font-semibold text-gray-700">Mon camion</h2>
            {profile && (
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                profile.isOnline ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {profile.isOnline ? 'En ligne' : 'Hors ligne'}
              </span>
            )}
          </div>

          {profileError ? (
            <div className="p-8 text-center text-gray-500">{profileError}</div>
          ) : !profile ? (
            <div className="p-8 text-center text-gray-400">Chargement…</div>
          ) : (
            <dl className="divide-y">
              <Detail label="Immatriculation" value={profile.plateNumber} strong />
              <Detail label="Marque / Modèle" value={[profile.brand, profile.model].filter(Boolean).join(' ')} />
              <Detail label="Type" value={VEHICLE_TYPE_LABELS[profile.vehicleType] || profile.vehicleType} />
              <Detail label="Capacité" value={profile.capacity} />
              <Detail label="VIN" value={profile.vin} />
              <Detail label="Téléphone" value={profile.phone} />
              <Detail label="Rayon de service" value={profile.serviceRadius ? `${profile.serviceRadius} km` : null} />
            </dl>
          )}
        </div>
      )}
    </div>
  );
};

export default LoaderDashboard;
